import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { OrganizationSettingsResponse } from '@bm/contracts';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, Input } from '../components/ui';
import { OrgTabs } from '../components/OrgTabs';
import { DEFAULT_NAV_ICONS, NAV_ICON_CHOICES, resolveIcon } from '../lib/nav-icons';

const NAV_ITEMS: Array<[string, string]> = [
  ['dashboard', 'Dashboard'],
  ['training', 'Training'],
  ['season', 'Saison'],
  ['calendar', 'Kalender'],
  ['equipment', 'Equipment'],
  ['administration', 'Verwaltung'],
];

function IconPicker({
  value,
  onPick,
  onClose,
}: {
  value: string;
  onPick: (name: string) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const choices = NAV_ICON_CHOICES.filter((n) => n.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3">
      <Input
        autoFocus
        placeholder="Icon suchen…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="mb-2"
      />
      {/* 6 statt fest 8 Spalten auf kleinen Bildschirmen — 8 Icons nebeneinander
       * ließen auf Handy-Breite kaum 36px je Berührungsfläche (§ Nutzeranfrage
       * „restliche Seiten … in der mobilen Ansicht anpassen"), 6 Spalten geben
       * spürbar mehr Platz pro Icon. */}
      <div className="grid max-h-64 grid-cols-6 gap-1 overflow-y-auto sm:grid-cols-8 md:grid-cols-10">
        {choices.map((name) => {
          const Icon = resolveIcon(name);
          return (
            <button
              key={name}
              type="button"
              title={name}
              onClick={() => {
                onPick(name);
                onClose();
              }}
              className={`flex items-center justify-center rounded-md p-2 hover:bg-slate-200 ${
                name === value ? 'bg-brand-accent text-white hover:bg-brand-accent' : 'text-slate-600'
              }`}
            >
              <Icon size={18} />
            </button>
          );
        })}
        {choices.length === 0 && (
          <p className="col-span-full py-2 text-center text-xs text-slate-400">Keine Treffer.</p>
        )}
      </div>
    </div>
  );
}

export function OrgNavigationPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const readOnly = !can('organization.edit');
  const [openFor, setOpenFor] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['org', 'settings'],
    queryFn: () => api<OrganizationSettingsResponse>('/org/settings', { orgScoped: true }),
  });

  const setIcon = useMutation({
    mutationFn: (navIcons: Record<string, string>) =>
      api<OrganizationSettingsResponse>('/org/settings', {
        method: 'PATCH',
        orgScoped: true,
        body: { navIcons },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['org', 'settings'] }),
  });

  const navIcons = data?.navIcons ?? {};

  return (
    <div className="space-y-4">
      <h1 className="hidden text-xl font-semibold md:block">Verein</h1>
      <OrgTabs />

      <Card title="Navigationsleiste">
        <p className="mb-4 text-xs text-slate-400">
          Icon je Hauptmenüpunkt wählen — erscheint sofort in der Navigation für alle Mitglieder des
          Vereins.
        </p>
        <div className="divide-y divide-slate-100">
          {NAV_ITEMS.map(([key, label]) => {
            const current = navIcons[key] ?? DEFAULT_NAV_ICONS[key]!;
            const Icon = resolveIcon(current);
            return (
              <div key={key} className="py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 text-slate-700">
                    <Icon size={18} />
                  </div>
                  <span className="flex-1 text-sm font-medium">{label}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      className="text-sm text-brand-accent hover:underline"
                      onClick={() => setOpenFor((o) => (o === key ? null : key))}
                    >
                      {openFor === key ? 'schließen' : 'ändern'}
                    </button>
                  )}
                </div>
                {openFor === key && (
                  <IconPicker
                    value={current}
                    onClose={() => setOpenFor(null)}
                    onPick={(name) => setIcon.mutate({ ...navIcons, [key]: name })}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
